import React, { forwardRef } from "react";
import { createPortal } from "react-dom";
import { FieldClearButton } from "../../atoms/Button/FieldClearButton.js";
import { cn } from "../../lib/utils.js";
import { useFormField } from "../../lib/form-field-context.js";
import { useOverlayPresence } from "../../primitives/OverlaySurface.js";
import {
  getLayerRoot,
  mergeRefs,
  useDismissable,
  useLayer,
  usePosition,
  useRovingTabIndex,
} from "../../primitives/index.js";
import type { ComboboxOption, ComboboxProps } from "./Combobox.types.js";
import { ComboboxListbox } from "./ComboboxListbox.js";
import { useComboboxRows } from "./useComboboxRows.js";

/* ---------------- Combobox (C8 #3) ----------------
   Editable single-select on the overlay engine (strategy §15.3, §2.4). APG
   "combobox with listbox popup": a text input (role=combobox, aria-expanded,
   aria-controls, aria-autocomplete=list) over a portaled listbox; focus stays
   on the input and the active option is tracked via aria-activedescendant +
   roving index. Typing filters; ArrowUp/Down move; Enter selects; Esc closes
   and reverts the text to the selection.

   `options` filters on the client; `loadOptions` loads cursor pages while the
   listbox is open (useComboboxRows). Multi-select is the §2.4 follow-up.
   (Non-text listboxes use Select + the useTypeahead primitive.) */

export type {
  ComboboxAsyncProps,
  ComboboxLoadRequest,
  ComboboxOption,
  ComboboxPage,
  ComboboxProps,
  ComboboxStaticProps,
} from "./Combobox.types.js";

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  function Combobox(
    {
      value,
      defaultValue,
      onChange,
      label,
      hint,
      placeholder = "Search…",
      disabled,
      id,
      name,
      className,
      emptyMessage = "No matches",
      clearable,
      clearLabel = "Clear selection",
      // options | loadOptions, and the props that go with each.
      ...rowsSource
    },
    forwardedRef,
  ) {
    const autoId = React.useId();
    const cbId = id ?? autoId;
    const listId = `${cbId}-list`;
    const optId = (i: number) => `${cbId}-opt-${i}`;
    const field = useFormField();

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<
      string | undefined
    >(defaultValue);
    const current = isControlled ? value : internalValue;

    const [open, setOpen] = React.useState(false);
    const {
      remoteMode,
      rows,
      selectedLabel,
      query,
      setQuery,
      setSearch,
      setPicked,
    } = useComboboxRows({ ...rowsSource, emptyMessage, current, open });
    const itemCount = rows.options.length + (rows.action ? 1 : 0);

    const layerId = useLayer({ open });
    const {
      activeIndex,
      setActiveIndex,
      onKeyDown: rovingKeyDown,
    } = useRovingTabIndex({ itemCount });

    const wrapRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLUListElement>(null);

    /* The listbox used to be destroyed in the same frame `open` flipped, so no
       CSS could give it an exit (ADR-0091). `present` keeps it mounted through
       that exit and `motionProps` makes it inert and aria-hidden while it runs.
       Everything that COMMITS, listens or registers still follows `open` — the
       logical state — so a listbox on its way out neither answers the keyboard
       nor swallows the click meant for whatever is behind it. */
    const { present, closing, motionProps } = useOverlayPresence(open, {
      ref: listRef,
    });

    /* What the exit animates is what was on screen when it began. Every close
       path rewrites what the rows derive from — Escape reverts `query` to the
       selection, a commit sets it to the picked label, an async close resets
       the search — so recomputing through the close would repopulate a fading
       list with rows the reader never asked for, overflowing the box the
       engine measured for the ones it was showing.

       So each close CAPTURES the rows on screen and the exit renders that
       capture. Taken in the close itself, not derived from `closing`: the
       presence hook derives its phase during render, so the render that first
       sees `open === false` still reports the previous phase. */
    const onScreen = React.useRef({ rows, activeIndex });
    React.useLayoutEffect(() => {
      if (open) onScreen.current = { rows, activeIndex };
    });
    const [leaving, setLeaving] = React.useState<
      typeof onScreen.current | null
    >(null);
    const close = React.useCallback(() => {
      setLeaving(onScreen.current);
      setOpen(false);
    }, []);

    const closeAndRevert = React.useCallback(() => {
      close();
      setQuery(selectedLabel);
      setSearch("");
    }, [close, selectedLabel, setQuery, setSearch]);

    const insideRefs = React.useMemo(() => [wrapRef, listRef], []);
    useDismissable({ layerId, open, onDismiss: closeAndRevert, insideRefs });

    const pos = usePosition({
      // `present`, not `open`: measuring against an input the listbox has
      // already logically left drops the placement and flies it to the corner
      // halfway through its fade.
      open: present,
      triggerRef: inputRef,
      contentRef: listRef,
      align: "start",
      sideOffset: 4,
      matchWidth: true,
    });

    React.useEffect(() => {
      setActiveIndex(0);
    }, [query, open, setActiveIndex]);

    React.useEffect(() => {
      if (!open || !listRef.current) return;
      const el = listRef.current.querySelectorAll('[role="option"]')[
        activeIndex
      ] as HTMLLIElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }, [open, activeIndex]);

    /* Set while committing or clearing, so the refocus below does not reopen
       the listbox. `onFocus` opens on purpose — that is how clicking the field
       reveals the options — but a commit calls `focus()` to return the caret to
       the input, and letting that reopen had two costs: the list reappeared
       immediately after picking, and, worse, the `!open` guard on the
       external-value sync above never ran. A CONTROLLED consumer that rejects
       the selection (asks "are you sure?" and the author declines) was then
       left displaying the option it had refused. A ref rather than state: it
       must be readable in the same tick as the focus event, before any
       re-render. */
    const committing = React.useRef(false);
    const refocus = () => {
      committing.current = true;
      inputRef.current?.focus();
      committing.current = false;
    };

    const commit = (opt: ComboboxOption) => {
      if (closing) return;
      if (!isControlled) setInternalValue(opt.value);
      if (remoteMode) setPicked(opt);
      onChange?.(opt.value);
      setQuery(opt.label);
      setSearch("");
      close();
      refocus();
    };

    // The input text follows the emptied selection through the sync above,
    // so a controlled consumer that refuses the clear keeps its label.
    const clear = () => {
      if (!isControlled) setInternalValue("");
      onChange?.("");
      setSearch("");
      close();
      refocus();
    };

    const shown = !open && leaving ? leaving : { rows, activeIndex };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        rovingKeyDown(e);
        return;
      }
      if (!open) return;
      if (e.key === "Enter") {
        e.preventDefault();
        const opt = rows.options[activeIndex];
        if (opt) commit(opt);
        else if (rows.action && activeIndex === rows.options.length)
          rows.action.run();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeAndRevert();
        return;
      }
      if (e.key === "Home" || e.key === "End") rovingKeyDown(e);
    };

    const input = (
      <input
        ref={mergeRefs(inputRef, forwardedRef)}
        id={cbId}
        role="combobox"
        type="text"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={
          open && itemCount ? optId(activeIndex) : undefined
        }
        aria-describedby={field?.describedBy}
        aria-invalid={field?.invalid || undefined}
        aria-required={field?.required || undefined}
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => !disabled && !committing.current && setOpen(true)}
        onKeyDown={onKeyDown}
        className={cn(
          "w-full rounded-md border bg-pb-card text-sm text-pb-text px-3 h-9 transition-colors",
          "placeholder:text-pb-text-muted",
          // The text-field focus look (ADR-0092 d8); `aria-expanded` holds it
          // while the list is open.
          field?.invalid
            ? "border-pb-red/60 focus-frame-invalid"
            : "border-pb-control-border focus-frame",
          disabled && "opacity-60 cursor-not-allowed",
          // The clear control is a sibling, so the input gives up its fixed
          // w-full for the space left in the row it now shares.
          clearable && "w-auto min-w-0 flex-1",
          className,
        )}
      />
    );

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={cbId}
            className="block text-xs font-medium text-pb-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative" ref={wrapRef}>
          {clearable ? (
            <div className="flex items-center gap-1.5">
              {input}
              {!disabled && current ? (
                <FieldClearButton clearLabel={clearLabel} onClear={clear} />
              ) : null}
            </div>
          ) : (
            input
          )}
          {name && <input type="hidden" name={name} value={current ?? ""} />}
          {/* Mounted for the field's life, so each change is announced. */}
          {remoteMode ? (
            <span role="status" className="sr-only">
              {open ? rows.status : ""}
            </span>
          ) : null}
        </div>
        {present &&
          typeof document !== "undefined" &&
          createPortal(
            <ComboboxListbox
              listRef={listRef}
              id={listId}
              optionId={optId}
              rows={shown.rows}
              remote={remoteMode}
              activeIndex={shown.activeIndex}
              selectedValue={current}
              emptyMessage={emptyMessage}
              motionProps={motionProps}
              position={pos}
              onActiveIndexChange={setActiveIndex}
              onPick={commit}
              onAction={() => !closing && rows.action?.run()}
            />,
            getLayerRoot(),
          )}
        {hint && <p className="text-dense-1 text-pb-text-muted mt-1">{hint}</p>}
      </div>
    );
  },
);
